import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaTools,
  FaCommentDots,
  FaNetworkWired,
  FaDesktop,
  FaLaptop,
  FaServer,
  FaVideo,
  FaCamera,
  FaCog,
  FaMapMarkerAlt,
  FaRegClock,
  FaWhatsapp,
} from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

const servicesList = [
  { icon: <FaNetworkWired />, label: "Networking AMC" },
  { icon: <FaDesktop />, label: "Computer AMC" },
  { icon: <FaLaptop />, label: "Laptop AMC" },
  { icon: <FaServer />, label: "Server AMC" },
  { icon: <FaVideo />, label: "Projector AMC" },
  { icon: <FaCamera />, label: "CCTV AMC" },
  { icon: <FaTools />, label: "CCTV Installation" },
  { icon: <FaCog />, label: "All IT Services" },
];
const contactNumbers = [
  { num: "9217208800" },
  { num: "9217209900" },
  { num: "9217204400" },
];

// ANIMATION CONSTANTS for slower, higher movement
const INITIAL_Y = 32; // Original Tailwind: 14*0.25rem = 3.5rem = 56px, 32 = 8rem = 128px
const DURATION = 1800; // ms (original 800), much slower
const SERVICE_INITIAL_Y = 16; // Tailwind: 8*0.25rem = 2rem = 32px, now 16 = 4rem = 64px
const SERVICE_DURATION = 1400; // ms, for grid items
const CONTACT_INITIAL_Y = 8; // Tailwind: 4*0.25=1rem=16px, now 8=2rem=32px
const CONTACT_DURATION = 1200; // ms

export default function BookingForm() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Animation triggers when section is visible by 60px
  const isVisible = useOnScreen(sectionRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Animation inline style for main columns (slower upward movement)
  const mainAnimStyle = {
    transition: `all ${DURATION}ms cubic-bezier(0.33,0.75,0.68,1)`,
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated
      ? "translateY(0px)"
      : `translateY(${INITIAL_Y}px)`,
  };

  // Fixed background image (can swap URL as needed)
  const bgImageUrl = "/img/img5.jpeg";

  return (
    <section
      ref={sectionRef}
      className="relative py-20 font-PTSerif-Regular"
      style={{
        backgroundImage: `url('${bgImageUrl}')`,
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay for contrast */}
      <div className="absolute inset-0 bg-gray-100/55 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <div
          className="bg-white shadow-xl rounded-lg grid md:grid-cols-2 overflow-hidden"
          style={mainAnimStyle}
        >
          {/* LEFT INFO REDESIGNED */}
          <div
            className={
              "relative bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white px-10 py-12 flex flex-col justify-between rounded-t-lg md:rounded-l-lg md:rounded-tr-none font-PTSerif-Regular"
            }
            style={{
              ...mainAnimStyle,
              transitionDelay: hasAnimated ? "100ms" : "0ms",
            }}
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FaTools className="text-3xl" />
                <h2 className="text-3xl font-bold tracking-tight">Request IT Service</h2>
              </div>
              <div className="w-16 h-[3px] bg-white mb-6 rounded"></div>
              <p className="text-base opacity-90 leading-relaxed mb-6">
                Fill out the service request form and{" "}
                <span className="underline font-semibold">our IT team</span> will reach out promptly to schedule your appointment.
              </p>

              {/* Services grid */}
              <div className="mt-6 mb-10">
                <h4 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <FaCog className="inline-block" /> Our Key Services
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {servicesList.map((service, idx) => (
                    <div
                      key={service.label}
                      className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded px-2 py-1 transition-all"
                      style={{
                        transition: `all ${SERVICE_DURATION}ms cubic-bezier(0.33,0.75,0.68,1)`,
                        transitionDelay: hasAnimated
                          ? `${150 + idx * 90}ms`
                          : "0ms",
                        opacity: hasAnimated ? 1 : 0,
                        transform: hasAnimated
                          ? "translateY(0px)"
                          : `translateY(${SERVICE_INITIAL_Y}px)`,
                      }}
                    >
                      <span className="text-blue-200">{service.icon}</span>
                      <span>{service.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact/Support */}
              <div className="mt-8 text-sm space-y-3">
                <div className="flex gap-3 items-center flex-wrap">
                  <span className="font-semibold text-white text-base flex items-center gap-2">
                    <FaPhoneAlt className="mr-1" /> Call/WhatsApp:
                  </span>
                  {contactNumbers.map((item, idx) => (
                    <span
                      key={item.num}
                      className="bg-white/10 rounded py-1 px-3 flex items-center gap-1 hover:bg-blue-900 transition-all"
                      style={{
                        transition: `all ${CONTACT_DURATION}ms cubic-bezier(0.33,0.75,0.68,1)`,
                        transitionDelay: hasAnimated
                          ? `${300 + idx * 80}ms`
                          : "0ms",
                        opacity: hasAnimated ? 1 : 0,
                        transform: hasAnimated
                          ? "translateY(0px)"
                          : `translateY(${CONTACT_INITIAL_Y}px)`,
                      }}
                    >
                      <FaWhatsapp className="text-green-300 mr-0.5" />{" "}
                      {item.num}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <FaEnvelope className="inline text-blue-200" />{" "}
                  <a
                    href="mailto:straliapvtltd@gmail.com"
                    className="underline hover:text-blue-100 transition"
                  >
                    straliapvtltd@gmail.com
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <FaMapMarkerAlt className="inline text-blue-200 mt-0.5" />
                  <span className="text-xs">
                    Office No. 179-A First Floor (B/P),
                    <br />
                    Jivan Nagar, Ashram, South East Delhi - 110014
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FaRegClock className="inline text-blue-200" />
                  <span className="text-xs">Mon-Sat: 9:30 AM – 7:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div
            className="p-10 font-PTSerif-Regular"
            style={{
              ...mainAnimStyle,
              transitionDelay: hasAnimated ? "300ms" : "0ms",
            }}
          >
            <h3 className="text-2xl font-semibold mb-8">
              Book Appointment
            </h3>

            <form className="space-y-5">
              {/* Name */}
              <div className="relative">
                <FaUser className="absolute left-4 top-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full pl-12 pr-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-4 text-gray-400" />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full pl-12 pr-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
                />
              </div>

              {/* Phone */}
              <div className="relative">
                <FaPhoneAlt className="absolute left-4 top-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Phone Number"
                  className="w-full pl-12 pr-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
                />
              </div>

              {/* Services */}
              <div className="relative">
                <FaTools className="absolute left-4 top-4 text-gray-400" />
                <select
                  className="w-full pl-12 pr-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
                >
                  <option>Select Service</option>
                  <option>Networking AMC</option>
                  <option>Computer AMC</option>
                  <option>Laptop AMC</option>
                  <option>Server AMC</option>
                  <option>Projector AMC</option>
                  <option>CCTV AMC</option>
                  <option>CCTV Installation</option>
                  <option>All IT Services</option>
                </select>
              </div>

              {/* Message */}
              <div className="relative">
                <FaCommentDots className="absolute left-4 top-4 text-gray-400" />
                <textarea
                  rows="4"
                  placeholder="Describe your issue..."
                  className="w-full pl-12 pr-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
                ></textarea>
              </div>

              {/* Button */}
              <button
                className="w-full bg-blue-600 text-white py-3 rounded-md font-semibold hover:bg-blue-700 transition font-PTSerif-Regular"
              >
                Get Appointment
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}